import { type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import type { Address } from 'viem';

import FunctionKey from 'constants/functionKey';
import { useChainId, usePublicClient } from 'libs/wallet';

import { type VTokenWrapperKind, getVTokenWrapperKind } from './getVTokenWrapperKind';

interface TrimmedInput {
  underlyingAddress: Address;
}

type Options = QueryObserverOptions<
  VTokenWrapperKind,
  Error,
  VTokenWrapperKind,
  VTokenWrapperKind,
  [FunctionKey.GET_V_TOKEN_WRAPPER_KIND, { chainId: number; underlyingAddress: Address }]
>;

export const useGetVTokenWrapperKind = (
  { underlyingAddress }: TrimmedInput,
  options?: Partial<Options>,
) => {
  const { chainId } = useChainId();
  const { publicClient } = usePublicClient();

  return useQuery({
    queryKey: [FunctionKey.GET_V_TOKEN_WRAPPER_KIND, { chainId, underlyingAddress }],
    queryFn: () => getVTokenWrapperKind({ publicClient, underlyingAddress }),
    // Wrapper kind changes only on a proxy upgrade (WsTAOUpgradeable /
    // WAlphaUpgradeable) or on a transient RPC fault that returns zero data.
    // Finite times let both self-heal while the retained cache still serves
    // revisits instantly (stale-while-revalidate, no flicker).
    staleTime: 10 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    ...options,
  });
};
